import styled from 'styled-components';

import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { simpleDateAtTime } from 'lib/date';
import { adminIndexClients } from 'services/api/admin/clients';
import {
  type AdminCoordinationRequest,
  adminCoordinationRequestsQueryKeys,
  adminIndexCoordinationRequests,
  type CoordinationRequestStatus,
} from 'services/api/admin/coordinationRequests';
import { colors } from 'styles/theme/colors';

const STATUS_LABELS: Record<CoordinationRequestStatus, string> = {
  pending: 'Pending',
  auto_completed: 'Auto-Completed',
  completed: 'Completed',
  auto_dismissed: 'Auto-Dismissed',
  dismissed: 'Dismissed',
};

const STATUS_PILL_COLORS: Record<CoordinationRequestStatus, { background: string; color: string }> = {
  pending: { background: colors.yellow100, color: colors.black100 },
  auto_completed: { background: colors.green100, color: 'white' },
  completed: { background: colors.green100, color: 'white' },
  auto_dismissed: { background: colors.black10, color: colors.black100 },
  dismissed: { background: colors.black10, color: colors.black100 },
};

function StatusPill({ status }: { status: CoordinationRequestStatus }) {
  const pillColors = STATUS_PILL_COLORS[status];

  if (!pillColors) return <span>{status || '—'}</span>;

  return (
    <Pill $background={pillColors.background} $color={pillColors.color}>
      {STATUS_LABELS[status]}
    </Pill>
  );
}

const Pill = styled.span<{ $background: string; $color: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  background-color: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
`;

const STATUS_OPTIONS = (Object.keys(STATUS_LABELS) as CoordinationRequestStatus[]).map((value) => ({
  id: value,
  name: STATUS_LABELS[value],
}));

const SOURCE_OPTIONS = [
  { id: 'integration', name: 'Integration' },
  { id: 'user', name: 'User' },
];

const coordinationRequestsConfig: AdminListPageConfig<AdminCoordinationRequest> = {
  title: 'Coordination Requests',
  columnStorageKey: 'admin.coordinationRequests',
  queryKey: adminCoordinationRequestsQueryKeys.index(),
  fetchFn: (params, signal) => adminIndexCoordinationRequests(params, signal),
  fetchParams: { defaultSort: 'created_at desc' },
  searchPlaceholder: 'Search by Patient Name',
  showResultCount: true,

  filters: [
    {
      key: 'status',
      label: 'Status',
      type: 'multi_select',
      staticOptions: STATUS_OPTIONS,
    },
    {
      key: 'client',
      label: 'Client',
      type: 'multi_select',
      fetchConfig: {
        queryKey: ['adminClients', 'coordinationRequests', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
    },
    {
      key: 'source',
      label: 'Source',
      type: 'multi_select',
      staticOptions: SOURCE_OPTIONS,
    },
    {
      key: 'created_at',
      label: 'Date Range',
      type: 'date_range',
      defaultRange: 90,
    },
  ],

  columns: [
    {
      id: 'patient_name',
      header: 'Patient Name',
      render: (row) => row.patientName || '—',
    },
    {
      id: 'client',
      header: 'Client',
      render: (row) => row.client?.name || '—',
    },
    {
      id: 'status',
      header: 'Status',
      render: (row) => <StatusPill status={row.status} />,
    },
    {
      id: 'submitted_by',
      header: 'Submitted By',
      render: (row) => row.submittedBy || '—',
    },
    {
      id: 'user',
      header: 'User',
      render: (row) => row.user || '—',
    },
    {
      id: 'created_at',
      header: 'Date Requested',
      render: (row) => (row.createdAt ? simpleDateAtTime(row.createdAt) : '—'),
    },
  ],
};

export default function CoordinationRequests() {
  const state = useAdminListPage(coordinationRequestsConfig);

  return <AdminListPageRenderer config={coordinationRequestsConfig} state={state} />;
}
